import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import Dropdown from "../../common/dropDown";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter from "../../common/DateRangeFilter";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import { fetchAssignableStaffLookup } from "../../../services/lookupService";
import {
  archiveDocumentControl,
  fetchDocumentCategories,
  fetchDocumentControlList,
  fetchDocumentTypes,
  transferDocumentOwnership,
} from "./qmsService";
import {
  apiMessage,
  formatVaultApiError,
  isApiSuccess,
  normalizeSuccessStoryList,
  pickApiRowValue,
} from "./qmsApiUtils";
import { getQmsAccessState, useQmsAccess } from "./qmsPermissionUtils";
import {
  getVaultCategoryDisplay,
  getVaultDocumentListId,
  getVaultStageClass,
  getVaultTypeDisplay,
  resolveVaultDocumentStage,
} from "./qmsVaultUtils";
import { getQmsScreen } from "./qmsScreenConfig";
import { canAccessDocumentControl } from "./documentControlConfig";
import {
  applyDocumentControlFilters,
  buildDocumentControlStageOptions,
  buildDocumentControlStatusOptions,
  createInitialDocumentControlFilters,
  DOCUMENT_CONTROL_COLUMN_DATE_PRESETS,
  mapDocumentControlLookupOptions,
} from "./documentControlFilterUtils";
import {
  canArchiveDocumentControlRow,
  canTransferDocumentControlRow,
  DEFAULT_DOCUMENT_CONTROL_COLUMNS,
  DOCUMENT_CONTROL_COLUMN_HEADER_CLASS,
  DOCUMENT_CONTROL_FIELD_OPTIONS,
  getDocumentControlFieldLabel,
  getDocumentControlList,
  getDocumentControlTitle,
} from "./documentControlUtils";
import QmsDocumentPreviewModal from "./QmsDocumentPreviewModal";
import "./QmsPages.scss";

const screen = getQmsScreen("documentControl");
const VAULT_CELL_MAX_LEN = 28;

function truncateText(text, maxLen = VAULT_CELL_MAX_LEN) {
  const value = String(text ?? "").trim() || "—";
  if (value === "—" || value.length <= maxLen) return { value, display: value };
  return { value, display: `${value.slice(0, maxLen)}…` };
}

function formatTruncatedCell(text, maxLen = VAULT_CELL_MAX_LEN) {
  const { value, display } = truncateText(text, maxLen);
  return (
    <span className="qms-vault__truncated" title={value}>
      {display}
    </span>
  );
}

function statusBadgeClass(status) {
  const value = String(status || "").trim().toLowerCase();
  if (value === "archived") return "qms-document-control__badge qms-document-control__badge--archived";
  if (value === "active") return "qms-document-control__badge qms-document-control__badge--active";
  return "qms-document-control__badge";
}

function isQmsHttpSuccess(response) {
  return Number(response?.success) === 1;
}

export default function QmsDocumentControl() {
  const { userData, loading: userLoading } = useUserData();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { screenAccess, isSystemAdmin } = useQmsAccess();

  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [staffRows, setStaffRows] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [typeNameByAbbr, setTypeNameByAbbr] = useState(() => new Map());
  const [categoryNameByAbbr, setCategoryNameByAbbr] = useState(() => new Map());
  const [tableFilters, setTableFilters] = useState(createInitialDocumentControlFilters);
  const [refreshToken, setRefreshToken] = useState(0);
  const [archiveModal, setArchiveModal] = useState({ open: false, row: null, comments: "" });
  const [transferModal, setTransferModal] = useState({
    open: false,
    row: null,
    newOwner: null,
    comments: "",
  });
  const [previewModal, setPreviewModal] = useState({ open: false, row: null });
  const [actionBusy, setActionBusy] = useState(false);

  const userDataRef = useRef(userData);
  const showAlertRef = useRef(showAlert);
  const fetchSeqRef = useRef(0);
  userDataRef.current = userData;
  showAlertRef.current = showAlert;

  const organizationId = userData?.OrganizationId || "";
  const staffId = userData?.StaffId || "";
  const canAccess = canAccessDocumentControl(screenAccess);
  const canArchive =
    isSystemAdmin ||
    screenAccess.QMS_ARCHIVE_DOCUMENTS ||
    screenAccess.QMS_DOCUMENT_CONTROL;
  const canTransfer =
    isSystemAdmin ||
    screenAccess.QMS_TRANSFER_OWNERSHIP ||
    screenAccess.QMS_DOCUMENT_CONTROL;

  const pageAccess = getQmsAccessState({
    userData,
    isSystemAdmin,
    permissionKey: null,
    loading: userLoading,
  });

  const staffOptions = useMemo(
    () =>
      (staffRows || []).map((staff) => ({
        label: staff.StaffName || staff.label || "Staff",
        value: staff.StaffId || staff.value,
      })),
    [staffRows],
  );

  const typeFilterOptions = useMemo(
    () => mapDocumentControlLookupOptions(documentTypes),
    [documentTypes],
  );

  const categoryFilterOptions = useMemo(
    () => mapDocumentControlLookupOptions(documentCategories),
    [documentCategories],
  );

  const stageFilterOptions = useMemo(
    () => buildDocumentControlStageOptions(documents),
    [documents],
  );

  const statusFilterOptions = useMemo(
    () => buildDocumentControlStatusOptions(documents),
    [documents],
  );

  const tableRows = useMemo(
    () => applyDocumentControlFilters(documents, tableFilters),
    [documents, tableFilters],
  );

  const loadDocuments = useCallback(async () => {
    const currentUser = userDataRef.current;
    if (!currentUser?.OrganizationId || !currentUser?.StaffId) return;

    const seq = ++fetchSeqRef.current;
    setDocumentsLoading(true);

    try {
      const response = await fetchDocumentControlList(currentUser);
      if (seq !== fetchSeqRef.current) return;
      if (!isApiSuccess(response)) {
        throw new Error(
          formatVaultApiError(apiMessage(response) || "Could not load document control list."),
        );
      }
      setDocuments(getDocumentControlList(response));
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      setDocuments([]);
      const message = error?.message || "Could not load document control list.";
      showAlertRef.current?.({
        title: "Error",
        message,
        type: "danger",
      });
    } finally {
      if (seq === fetchSeqRef.current) {
        setDocumentsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (pageAccess.status !== "allowed" || !canAccess || !organizationId || !staffId) {
      return undefined;
    }

    let cancelled = false;
    Promise.all([
      fetchAssignableStaffLookup(userDataRef.current),
      fetchDocumentTypes(userDataRef.current),
      fetchDocumentCategories(userDataRef.current),
    ])
      .then(([staff, typeRes, catRes]) => {
        if (cancelled) return;
        setStaffRows(Array.isArray(staff) ? staff : []);

        const types = isQmsHttpSuccess(typeRes)
          ? normalizeSuccessStoryList(typeRes).map((row) => ({
              id: String(row.DocumentTypeId),
              label: row.DocumentType,
              abbr: row.Abberiviation,
            }))
          : [];
        setDocumentTypes(types);

        const typeMap = new Map();
        types.forEach((row) => {
          if (row.abbr) typeMap.set(row.abbr, row.label || row.abbr);
        });
        setTypeNameByAbbr(typeMap);

        const categories = isQmsHttpSuccess(catRes)
          ? normalizeSuccessStoryList(catRes).map((row) => ({
              id: String(row.DocumentCategoryId),
              label: row.DocumentCategory,
              abbr: row.Abberiviation,
            }))
          : [];
        setDocumentCategories(categories);

        const catMap = new Map();
        categories.forEach((row) => {
          if (row.abbr) catMap.set(row.abbr, row.label || row.abbr);
        });
        setCategoryNameByAbbr(catMap);
      })
      .catch(() => {
        if (!cancelled) {
          setStaffRows([]);
          setDocumentTypes([]);
          setDocumentCategories([]);
          setTypeNameByAbbr(new Map());
          setCategoryNameByAbbr(new Map());
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pageAccess.status, canAccess, organizationId, staffId]);

  useEffect(() => {
    if (pageAccess.status !== "allowed" || !canAccess || !organizationId || !staffId) {
      return undefined;
    }
    loadDocuments();
    return undefined;
  }, [pageAccess.status, canAccess, organizationId, staffId, refreshToken, loadDocuments]);

  const handleRefresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const updateTableFilters = useCallback((patch) => {
    setTableFilters((current) => ({ ...current, ...patch }));
  }, []);

  const closeArchiveModal = useCallback(() => {
    setArchiveModal({ open: false, row: null, comments: "" });
  }, []);

  const closeTransferModal = useCallback(() => {
    setTransferModal({ open: false, row: null, newOwner: null, comments: "" });
  }, []);

  const openPreviewModal = useCallback((row) => {
    setPreviewModal({ open: true, row });
  }, []);

  const closePreviewModal = useCallback(() => {
    setPreviewModal({ open: false, row: null });
  }, []);

  const submitArchive = useCallback(async () => {
    const row = archiveModal.row;
    if (!row?.DocumentId || !userData) return;
    setActionBusy(true);
    try {
      await archiveDocumentControl(userData, {
        documentId: row.DocumentId,
        comments: archiveModal.comments,
      });
      showAlert({
        title: "Archived",
        message: "Document has been archived successfully.",
        type: "success",
      });
      closeArchiveModal();
      handleRefresh();
    } catch (error) {
      showAlert({
        title: "Archive failed",
        message: error?.message || "Could not archive document.",
        type: "danger",
      });
    } finally {
      setActionBusy(false);
    }
  }, [archiveModal, userData, showAlert, closeArchiveModal, handleRefresh]);

  const submitTransfer = useCallback(async () => {
    const row = transferModal.row;
    const newOwnerId = transferModal.newOwner?.value;
    if (!row?.DocumentId || !newOwnerId || !userData) return;
    setActionBusy(true);
    try {
      await transferDocumentOwnership(userData, {
        documentIds: String(row.DocumentId),
        newOwnerId,
        comments: transferModal.comments,
      });
      showAlert({
        title: "Ownership transferred",
        message: "Document owner has been updated.",
        type: "success",
      });
      closeTransferModal();
      handleRefresh();
    } catch (error) {
      showAlert({
        title: "Transfer failed",
        message: error?.message || "Could not transfer ownership.",
        type: "danger",
      });
    } finally {
      setActionBusy(false);
    }
  }, [transferModal, userData, showAlert, closeTransferModal, handleRefresh]);

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === "DocumentId") {
        const docId = getVaultDocumentListId(row);
        return (
          <span className="qms-doc-lib__doc-id" title={docId}>
            {docId}
          </span>
        );
      }
      if (field === "DocumentTitle") {
        const title = getDocumentControlTitle(row);
        const { value, display } = truncateText(title);
        return (
          <button
            type="button"
            className="report-table__cell-link qms-document-control__doc-title-link"
            onClick={() => openPreviewModal(row)}
            title={value}
          >
            {display}
          </button>
        );
      }
      if (field === "DocumentType") {
        return formatTruncatedCell(getVaultTypeDisplay(row, typeNameByAbbr));
      }
      if (field === "DocumentCategory") {
        return formatTruncatedCell(getVaultCategoryDisplay(row, categoryNameByAbbr));
      }
      if (field === "DocumentStage") {
        const stage = resolveVaultDocumentStage(row?.DocumentStage);
        return (
          <span className={`qms-vault__stage ${getVaultStageClass(stage)}`}>{stage}</span>
        );
      }
      if (field === "ControlStatus") {
        const status = String(row?.ControlStatus || "—");
        const showArchiveBtn = canArchive && canArchiveDocumentControlRow(row);
        return (
          <div className="qms-document-control__status-cell">
            <span className={statusBadgeClass(status)}>{status}</span>
            {showArchiveBtn ? (
              <button
                type="button"
                className="qms-document-control__icon-btn is-archive"
                disabled={actionBusy}
                title="Archive document"
                aria-label="Archive document"
                onClick={() => setArchiveModal({ open: true, row, comments: "" })}
              >
                <i className="fas fa-trash-alt" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        );
      }
      if (field === "CreatedBy") {
        const ownerName = String(row?.CreatedBy || "—").trim() || "—";
        const showTransferBtn = canTransfer && canTransferDocumentControlRow(row);
        return (
          <div className="qms-document-control__owner-cell">
            <span className="qms-document-control__owner-name" title={ownerName}>
              {formatTruncatedCell(ownerName)}
            </span>
            {showTransferBtn ? (
              <button
                type="button"
                className="qms-document-control__icon-btn is-transfer"
                disabled={actionBusy}
                title="Transfer ownership"
                aria-label="Transfer ownership"
                onClick={() =>
                  setTransferModal({ open: true, row, newOwner: null, comments: "" })
                }
              >
                <i className="fas fa-exchange-alt" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        );
      }
      if (field === "PublishStatus") {
        return formatTruncatedCell(row?.PublishStatus);
      }
      return formatTruncatedCell(row?.[field]);
    },
    [actionBusy, canArchive, canTransfer, categoryNameByAbbr, openPreviewModal, typeNameByAbbr],
  );

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    const raw = String(searchValue || "").trim();
    if (!raw) return true;

    if (
      field === "DocumentType" ||
      field === "DocumentCategory" ||
      field === "DocumentStage" ||
      field === "ControlStatus" ||
      field === "PublishedDate" ||
      field === "SystemDateTime"
    ) {
      return true;
    }

    if (field === "DocumentId") {
      const listId = getVaultDocumentListId(row).toLowerCase();
      const term = raw.toLowerCase();
      const digits = term.replace(/\D/g, "");
      if (digits) {
        return listId.replace(/\D/g, "").includes(digits) || listId.includes(term);
      }
      return listId.includes(term);
    }

    if (field === "DocumentTitle") {
      return getDocumentControlTitle(row).toLowerCase().includes(raw.toLowerCase());
    }

    if (field === "CreatedBy") {
      return String(row?.CreatedBy || "")
        .toLowerCase()
        .includes(raw.toLowerCase());
    }

    return String(row[field] ?? "")
      .toLowerCase()
      .includes(raw.toLowerCase());
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "DocumentId" || field === "DocumentTitle") {
        if (options?.checkOnly) return true;
        return (
          <input
            type="search"
            className="report-table__column-search qms-doc-lib__column-filter"
            placeholder={field === "DocumentId" ? "Search ID" : "Search title"}
            value={options?.value || ""}
            disabled={options?.disabled}
            onChange={(event) => options?.onChange?.(event.target.value)}
          />
        );
      }

      if (field === "DocumentType") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-document-control__column-filter is-multiselect">
            <BadgeMultiSelect
              options={typeFilterOptions}
              value={tableFilters.typeAbbrs}
              onChange={(values) => updateTableFilters({ typeAbbrs: values })}
              emptyLabel="All types"
              addLabel="+"
              popoverTitle="Filter by type"
              popoverSubtitle="Select one or more document types"
              showSearch
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      if (field === "DocumentCategory") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-document-control__column-filter is-multiselect">
            <BadgeMultiSelect
              options={categoryFilterOptions}
              value={tableFilters.categoryAbbrs}
              onChange={(values) => updateTableFilters({ categoryAbbrs: values })}
              emptyLabel="All categories"
              addLabel="+"
              popoverTitle="Filter by category"
              popoverSubtitle="Select one or more categories"
              showSearch
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      if (field === "DocumentStage") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-document-control__column-filter is-multiselect">
            <BadgeMultiSelect
              options={stageFilterOptions}
              value={tableFilters.stages}
              onChange={(values) => updateTableFilters({ stages: values })}
              emptyLabel="All stages"
              addLabel="+"
              popoverTitle="Filter by stage"
              popoverSubtitle="Select one or more workflow stages"
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      if (field === "ControlStatus") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-document-control__column-filter is-multiselect">
            <BadgeMultiSelect
              options={statusFilterOptions}
              value={tableFilters.controlStatuses}
              onChange={(values) => updateTableFilters({ controlStatuses: values })}
              emptyLabel="All statuses"
              addLabel="+"
              popoverTitle="Filter by status"
              popoverSubtitle="Select Active or Archived"
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      if (field === "PublishedDate") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={tableFilters.publishedDateRange}
              onChange={(range) => updateTableFilters({ publishedDateRange: range })}
              placeholder="Published range"
              presetOptions={DOCUMENT_CONTROL_COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      if (field === "SystemDateTime") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={tableFilters.updatedDateRange}
              onChange={(range) => updateTableFilters({ updatedDateRange: range })}
              placeholder="Updated range"
              presetOptions={DOCUMENT_CONTROL_COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      if (field === "CreatedBy") {
        if (options?.checkOnly) return true;
        return (
          <input
            type="search"
            className="report-table__column-search qms-doc-lib__column-filter"
            placeholder="Search owner"
            value={options?.value || ""}
            disabled={options?.disabled}
            onChange={(event) => options?.onChange?.(event.target.value)}
          />
        );
      }

      return null;
    },
    [
      categoryFilterOptions,
      stageFilterOptions,
      statusFilterOptions,
      tableFilters,
      typeFilterOptions,
      updateTableFilters,
    ],
  );

  const layoutStorageKey = useMemo(
    () => (staffId ? `qms-document-control-layout-v4-${staffId}` : ""),
    [staffId],
  );

  const getColumnHeaderClassName = useCallback(
    (field) => DOCUMENT_CONTROL_COLUMN_HEADER_CLASS[field] || "",
    [],
  );

  const getColumnCellClassName = useCallback(
    (field) => DOCUMENT_CONTROL_COLUMN_HEADER_CLASS[field] || "",
    [],
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: "Home", path: "/dashboard" },
      { label: screen?.title || "Document Control" },
    ],
    [],
  );

  const breadcrumbActions = useMemo(
    () => [
      {
        icon: `fas fa-sync-alt${documentsLoading ? " fa-spin" : ""}`,
        label: "Refresh",
        onClick: handleRefresh,
      },
    ],
    [documentsLoading, handleRefresh],
  );

  if (pageAccess.status === "loading") {
    return (
      <div className="user-mgmt-page qms-document-library qms-document-control">
        <PageBreadcrumb items={breadcrumbItems} />
        <p className="text-muted mb-0 px-3 py-4">Loading…</p>
      </div>
    );
  }

  if (pageAccess.status === "denied") {
    return (
      <div className="user-mgmt-page qms-document-library qms-document-control">
        <PageBreadcrumb items={breadcrumbItems} />
        <div className="alert alert-warning mt-3" role="alert">
          <strong>Access restricted.</strong> {pageAccess.message}
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="user-mgmt-page qms-document-library qms-document-control">
        <PageBreadcrumb items={breadcrumbItems} />
        <div className="alert alert-warning mt-3" role="alert">
          <strong>Access restricted.</strong> You do not have permission for document
          control. Ask your administrator to assign Tracker, Transfer Ownership, or
          Archive Documents permissions.
        </div>
      </div>
    );
  }

  return (
    <div className="user-mgmt-page qms-document-library qms-document-control">
      <InfoAlert {...alertState} onClose={hideAlert} />
      <PageBreadcrumb items={breadcrumbItems} actions={breadcrumbActions} />

      <div className="qms-doc-lib__panel">
        <div className="qms-doc-lib__report-wrap">
          <ReportTable
            rows={tableRows}
            fieldOptions={DOCUMENT_CONTROL_FIELD_OPTIONS}
            defaultColumns={DEFAULT_DOCUMENT_CONTROL_COLUMNS}
            getFieldLabel={getDocumentControlFieldLabel}
            getColumnHeaderClassName={getColumnHeaderClassName}
            getColumnCellClassName={getColumnCellClassName}
            renderCell={renderReportCell}
            renderColumnSearch={renderColumnSearch}
            matchColumnSearch={matchColumnSearch}
            pageSizeOptions={[10, 25, 50, 100]}
            columnSearchMode="client"
            frozenColumnWidthRem={8}
            persistLayoutKey={layoutStorageKey}
            loading={documentsLoading}
            emptyMessage="No documents found."
            emptyDescription=""
          />
        </div>
      </div>

      {archiveModal.open ? (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Archive document</h5>
                <button type="button" className="btn-close" onClick={closeArchiveModal} />
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  Archive <strong>{getDocumentControlTitle(archiveModal.row)}</strong>?
                </p>
                <p className="text-muted small">
                  Only published documents can be archived. This marks the document group
                  as archived for your organisation.
                </p>
                <label className="form-label" htmlFor="archive-comments">
                  Comments (optional)
                </label>
                <textarea
                  id="archive-comments"
                  className="form-control"
                  rows={3}
                  value={archiveModal.comments}
                  onChange={(event) =>
                    setArchiveModal((current) => ({
                      ...current,
                      comments: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeArchiveModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={actionBusy}
                  onClick={submitArchive}
                >
                  {actionBusy ? "Archiving…" : "Archive"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {transferModal.open ? (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Transfer ownership</h5>
                <button type="button" className="btn-close" onClick={closeTransferModal} />
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Transfer <strong>{getDocumentControlTitle(transferModal.row)}</strong> to
                  a new owner.
                </p>
                <label className="form-label">New owner</label>
                <Dropdown
                  options={staffOptions}
                  value={transferModal.newOwner}
                  onChange={(option) =>
                    setTransferModal((current) => ({ ...current, newOwner: option }))
                  }
                  placeholder="Select staff"
                />
                <label className="form-label mt-3" htmlFor="transfer-comments">
                  Comments (optional)
                </label>
                <textarea
                  id="transfer-comments"
                  className="form-control"
                  rows={3}
                  value={transferModal.comments}
                  onChange={(event) =>
                    setTransferModal((current) => ({
                      ...current,
                      comments: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeTransferModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={actionBusy || !transferModal.newOwner?.value}
                  onClick={submitTransfer}
                >
                  {actionBusy ? "Transferring…" : "Transfer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <QmsDocumentPreviewModal
        open={previewModal.open}
        onClose={closePreviewModal}
        documentId={String(previewModal.row?.DocumentId || "")}
        documentUrl={
          pickApiRowValue(previewModal.row, "DocumentURL", "DocumentUrl", "documentUrl") || ""
        }
        documentExtension={
          pickApiRowValue(
            previewModal.row,
            "DocumentExtension",
            "documentExtension",
          ) || ""
        }
        documentTitle={getDocumentControlTitle(previewModal.row)}
        documentCode={getVaultDocumentListId(previewModal.row || {})}
      />
    </div>
  );
}
